import { z } from 'zod'
import interceptedAxios from '../../../utils/interceptedAxios'
import { type AdvancedFilterPostRequest, ZAssetForGroup, type AssetForGroup, type AdvancedFilterPostRequestBodyParsed } from '../types'

const getFields = (sortfield: string, groupId: string, input: string, assetTypeId: string): string => {
  let field = sortfield
  if (field === 'assetId') {
    field = 'id'
  } else if (field === 'state') {
    field = 'stateName'
  }
  if (assetTypeId !== '' && input === '' && groupId === '') {
    if (field === 'tag') {
      field = 'tag.name'
    }
  } else if (assetTypeId === '' && input === '' && groupId !== '') {
    // istanbul ignore else
    if (field === 'id') {
      field = 'assetId'
    }
  }
  return field
}

const ZApiResponse = z.object({
  http_status: z.number(),
  data: z.optional(ZAssetForGroup.nullable()),
  error: z.boolean(),
  error_message: z.optional(z.string())
})

export type ApiResponse = z.infer<typeof ZApiResponse>

// eslint-disable-next-line @typescript-eslint/no-unused-vars
let abortController: AbortController | null = null

export async function getAssetListForGroup (
  page: number,
  size: number,
  sortdir: string,
  sortfield: string,
  onSuccess: (assetForGroup: AssetForGroup, httpStatus: number) => any,
  onFailure: (errorMessage: any, httpStatus: number) => any,
  groupId: string,
  input: string,
  assetTypeId: string,
  searchInput: string
): Promise<void> {
  // Cancel the previous request if it exists
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (abortController) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    abortController.abort()
  }

  // Create a new AbortController
  abortController = new AbortController()
  const signal = abortController.signal
  const field = getFields(sortfield, groupId, input, assetTypeId)
  const url = `/asset-service/api/v1/udp/groupManagement/asset-group/assets?groupId=${groupId !== undefined ? groupId : ''}&input=${input !== undefined ? input : ''}&searchAssetsInput=${searchInput}&assetTypeId=${assetTypeId !== undefined ? assetTypeId : ''}&page=${page}&size=${size}&sort=${field},${sortdir}`

  interceptedAxios
    .get(url, { signal })
    .then((response) => {
      const parseResponse = ZApiResponse.safeParse(response.data)
      if (parseResponse.success) {
        if (
          parseResponse.data.data !== null &&
          parseResponse.data.data !== undefined
        ) {
          onSuccess(parseResponse.data.data, parseResponse.data.http_status)
        } else {
          onFailure(parseResponse.data.error_message, parseResponse.data.http_status)
        }
      } else {
        onFailure(parseResponse.error.message, response.status)
      }
      abortController = null
    })
    .catch((error) => {
      // istanbul ignore next
      if (error.name === 'CanceledError') {
        // Ignore the error if the request was aborted
        console.warn('Request was aborted:', error.message)
        return
      }
      // istanbul ignore else
      if (error.response !== undefined) {
        if (error.response.data !== undefined) {
          onFailure(error.response.data.error_message, error.response.status)
        } else {
          onFailure(error.message, error.response.status)
        }
      } else {
        onFailure(error.message, error.status)
      }
    })
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
let abortControllerAdvFilter: AbortController | null = null

// istanbul ignore file
export async function advancedFilter (
  advancedFilterPostRequest: AdvancedFilterPostRequest,
  page: number,
  size: number,
  groupId: string | null,
  sortdir: string,
  sortfield: string,
  searchPage: string,
  searchKey: string,
  onSuccess: (successResponse: AssetForGroup, httpStatus: number) => any,
  onFailure: (errorMessage: string, httpStatus: number) => any,
  assetType?: string
): Promise<void> {
  let url
  if (assetType !== undefined && assetType !== null) {
    url = `/search-service/api/v1/udp/search/assetSearch?page=${page}&size=${size}&groupId=${groupId ?? ''}&sort=${sortfield},${sortdir}&searchPage=${searchPage}&searchKey=${searchKey}&assetType=${assetType}`
  } else {
    url = `/search-service/api/v1/udp/search/assetSearch?page=${page}&size=${size}&groupId=${groupId ?? ''}&sort=${sortfield},${sortdir}&searchPage=${searchPage}&searchKey=${searchKey}`
  }
  // Cancel the previous request if it exists
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (abortControllerAdvFilter) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    abortControllerAdvFilter.abort()
  }

  // Create a new AbortController
  abortControllerAdvFilter = new AbortController()
  const signal = abortControllerAdvFilter.signal
  const parsedRequestBody: AdvancedFilterPostRequestBodyParsed = {
    conjuction: advancedFilterPostRequest.conjuction,
    conditions: advancedFilterPostRequest.conditions.map((cond) => {
      let rowVal: string | string[] | number | number[] | boolean | boolean[] = cond.value
      if (cond.operation === 'Between') {
        if (cond.datatype === 'double') {
          rowVal = [Number(cond.value[0]), Number(cond.value[1])]
        } else {
          rowVal = [cond.value[0], cond.value[1]]
        }
      } else {
        if (cond.datatype === 'double' && cond.key !== 'firmwareVersion') {
          if (Array.isArray(cond.value)) {
            rowVal = [Number(cond.value[0])]
          } else {
            rowVal = Number(cond.value)
          }
        } else {
          rowVal = cond.value
        }
      }
      return {
        key: cond.key,
        operation: cond.operation,
        value: rowVal
      }
    })
  }

  interceptedAxios
    .post(url, parsedRequestBody, { signal })
    .then((response) => {
      const parseResponse = ZApiResponse.safeParse(response.data)
      // istanbul ignore else
      if (parseResponse.success && parseResponse.data.data !== null && parseResponse.data.data !== undefined) {
        onSuccess(parseResponse.data.data, parseResponse.data.http_status)
      } else {
        onFailure('Error occurred while processing response', response.status)
      }
      abortControllerAdvFilter = null
    })
    .catch((error) => {
      // istanbul ignore next
      if (error.name === 'CanceledError') {
        // Ignore the error if the request was aborted
        console.warn('Request was aborted:', error.message)
        return
      }
      if (error.response !== undefined) {
        // istanbul ignore else
        if (error.response.data !== undefined) {
          onFailure(error.response.data.error_message, error.response.data.http_status)
        } else {
          onFailure(error.response.statusText, error.response.status)
        }
      } else {
        onFailure('Network Failure', error.status)
      }
    })
}
